/**
 * useResolvedOrgId Hook
 * Resolves the organization ID with a fallback direct query
 * when the organization context is null (e.g., multi-org users).
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';

export const useResolvedOrgId = (): { orgId: string | null; isResolving: boolean } => {
  const { organization } = useOrganization();
  const { user } = useAuth();
  const [fallbackOrgId, setFallbackOrgId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const attemptedRef = useRef(false);

  // Fallback: query directly
  useEffect(() => {
    if (organization?.id) {
      attemptedRef.current = false;
      setFallbackOrgId(null);
      setIsResolving(false);
      return;
    }

    if (!user?.id) {
      attemptedRef.current = false;
      setFallbackOrgId(null);
      setIsResolving(false);
      return;
    }

    if (attemptedRef.current || fallbackOrgId) return;

    attemptedRef.current = true;
    setIsResolving(true);

    (async () => {
      try {
        const { data } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (data?.organization_id) {
          setFallbackOrgId(data.organization_id);
        }
      } catch (err) {
        console.warn('[useResolvedOrgId] Fallback query failed:', err);
      } finally {
        setIsResolving(false);
      }
    })();
  }, [user?.id, organization?.id, fallbackOrgId]);

  return {
    orgId: organization?.id ?? fallbackOrgId,
    isResolving: organization?.id ? false : isResolving,
  };
};
